class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.searchOverlays = document.querySelectorAll(".search_overlay");
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    event.preventDefault();

    this.searchTerm = this.getQuery();

    if (
      !this.searchTerm.length ||
      this.querySelector('[aria-selected="true"] a')
    )
      return;

    // Save search term to local storage
    this.saveSearchTermToLocalStorage(this.searchTerm);

    this.input.form.submit();
  }

  saveSearchTermToLocalStorage(term) {
    if (!term) return;

    try {
      // Get existing search terms from local storage
      const storedTerms =
        JSON.parse(localStorage.getItem("lastSearchedKeywords")) || [];

      // Remove the term if it already exists to avoid duplicates
      const filteredTerms = storedTerms.filter(
        (storedTerm) => storedTerm !== term
      );

      // Add the new term at the beginning
      filteredTerms.unshift(term);

      // Keep only the most recent 5 terms
      const limitedTerms = filteredTerms.slice(0, 5);

      // Save back to local storage
      localStorage.setItem(
        "lastSearchedKeywords",
        JSON.stringify(limitedTerms)
      );
    } catch (error) {
      console.error("Error saving search term to local storage:", error);
    }
  }

  getLastSearchedKeywordsFromLocalStorage() {
    try {
      return JSON.parse(localStorage.getItem("lastSearchedKeywords")) || [];
    } catch (error) {
      console.error("Error retrieving search terms from local storage:", error);
      return [];
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showDefaultResults();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  showDefaultResults() {
    const mostSearchedKeywords = [
      "tasmanian tiger",
      "patch",
      "uf pro",
      "shorts",
      "helikon tex",
    ];
    const mostSearchedKeywordsMarkup = generateHTML(
      mostSearchedKeywords.map((keyword) => ({ text: keyword, count: 0 })),
      this.predictiveSearchResults.dataset.suggestionsT
    );
    const lastSearchedKeywords = this.getLastSearchedKeywordsFromLocalStorage();
    const lastSearchedMarkup = generateLastSearchedHTML(lastSearchedKeywords);

    const handles = [
      "asmc-namensschild-kunststoff",
      "bundeswehr-original-bw-abzeichen-fallschirmspringer",
      "fritzsch-rangabzeichen-schwarz",
    ];
    const bestsellerPromises = handles.map((handle) =>
      fetch(`${window.Shopify.routes.root}products/${handle}.js`)
        .then((res) => res.json())
        .then((product) => ({
          title: product.title,
          price: (product.price / 100).toFixed(2),
          image: product.featured_image,
          url: product.url,
          vendor: product.vendor,
        }))
        .catch(() => null)
    );

    Promise.all(bestsellerPromises).then((products) => {
      const bestsellerMarkup = generateBestsellerHTML(products.filter(Boolean));
      const resultsMarkup = wrapMarkup(
        mostSearchedKeywordsMarkup,
        "",
        lastSearchedMarkup,
        bestsellerMarkup,
        "",
        ""
      );
      this.renderSearchResults(resultsMarkup);
    });
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.textContent;
    if (currentButtonText) {
      const test = new RegExp(previousTerm, "g").exec(currentButtonText);
      if (test !== null) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    const resourceFields = [
      'title',
      'product_type',
      'variants.title',
      'vendor',
      'variants.sku'
    ].join(',');

    const url = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(
      searchTerm,
    )}&resources[type]=product,collection,article,page,query&resources[options][fields]=${encodeURIComponent(resourceFields)}`;

    this.abortController.abort();
    this.abortController = new AbortController();

    fetch(url, { signal: this.abortController.signal })
      .then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(({ resources: { results } }) => {
        console.log(results);
        const keywordsMarkup = results.queries?.length
          ? generateHTML(
              results.queries,
              this.predictiveSearchResults.dataset.suggestionsT
            )
          : "";

        const fallbackImage =
          window.settings.autocomplete_image_fallback ||
          "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png?v=1530129081";
        const collectionsMarkup = generateCollectionsHTML(
          results.collections,
          fallbackImage
        );
        const productsMarkup = generateProductHTML(
          results.products || [],
          "",
          []
        );
        const pagesMarkup = generatePagesHTML(
          results.pages.concat(results.articles)
        );

        const resultsMarkup = wrapMarkup(
          keywordsMarkup,
          collectionsMarkup + productsMarkup + pagesMarkup,
          "",
          "",
          queryKey,
          this.predictiveSearchResults.dataset.drawerSearchT
        );

        this.allPredictiveSearchInstances.forEach(
          (i) => (i.cachedResults[queryKey] = resultsMarkup)
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Error fetching search results:", error);
          this.close();
        }
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.open();
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    this.searchOverlays.forEach((overlay) => {
      overlay.classList.add("active");
    });
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.searchOverlays.forEach((overlay) => {
      overlay.classList.remove("active");
    });
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);

const generateHTML = (queries) => {
  const keywordItems = queries
    .map(
      (query, index) => `
    <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-option-query-${
      index + 1
    }">
      <a href="/search?q=${encodeURIComponent(query.text)}&options[prefix]=last"
         class="predictive-search__item link link--text js-keyword"
         tabindex="-1">
        <div class="predictive-search__item-content predictive-search__item-content--centered">
          <p class="predictive-search__item-heading h5">
            ${query.text}
          </p>
        </div>
      </a>
    </li>
  `
    )
    .join("");

  const domain = window.location.hostname;
  const suggestionsStyle = domain.includes("asmc-unlimited")
    ? "color: black!important;"
    : "";
  return `
    <div class="predictive-search__result-group">
      <div>
        <h2
          id="predictive-search-queries"
          class="predictive-search__heading text-body caption-with-letter-spacing"
          style="${suggestionsStyle}"
        >
          Meistgesuchte Begriffe
        </h2>
        <ul
          id="predictive-search-results-queries-list"
          class="predictive-search__results-list list-unstyled"
          role="group"
          aria-labelledby="predictive-search-queries"
        >
          ${keywordItems}
        </ul>
      </div>
    </div>
  `;
};

const generateLastSearchedHTML = (keywords) => {
  if (!keywords || keywords.length === 0) return "";

  const keywordItems = keywords
    .map(
      (keyword, index) => `
    <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-last-searched-${
      index + 1
    }">
      <a href="/search?q=${encodeURIComponent(keyword)}&options[prefix]=last"
         class="predictive-search__item link link--text js-keyword"
         tabindex="-1">
        <div class="predictive-search__item-content predictive-search__item-content--centered">
          <p class="predictive-search__item-heading h5">
            ${keyword}
          </p>
        </div>
      </a>
    </li>
  `
    )
    .join("");

  return `
    <div class="predictive-search__result-group">
      <div>
        <h2
          id="predictive-search-last-searched"
          class="predictive-search__heading text-body caption-with-letter-spacing"
        >
          Zuletzt gesucht
        </h2>
        <ul
          id="predictive-search-results-last-searched-list"
          class="predictive-search__results-list list-unstyled"
          role="group"
          aria-labelledby="predictive-search-last-searched"
        >
          ${keywordItems}
        </ul>
      </div>
    </div>
  `;
};

const generateBestsellerHTML = (products) => {
  const productItems = products
    .map(
      (product, index) => `
    <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-bestseller-${
      index + 1
    }">
      <a href="${
        product.url
      }" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text js-product" tabindex="-1">
        <img class="predictive-search__image" src="${product.image}" alt="${
        product.title
      }" width="50" height="28.125" />
        <div class="predictive-search__item-content">
          <p class="predictive-search__item-heading h5">${product.vendor} ${
        product.title
      }</p>
          <div class="predictive-search__item-meta">
            <div class="predictive-search__item-prices">
              <span class="predictive-search__price">${product.price}€</span>
            </div>
          </div>
        </div>
      </a>
    </li>
  `
    )
    .join("");

  return `
    <div class="predictive-search__result-group">
      <div>
        <h2
          id="predictive-search-bestsellers"
          class="predictive-search__heading text-body caption-with-letter-spacing"
        >
          Bestseller
        </h2>
        <ul
          id="predictive-search-results-bestsellers-list"
          class="predictive-search__results-list list-unstyled"
          role="group"
          aria-labelledby="predictive-search-bestsellers"
        >
          ${productItems}
        </ul>
      </div>
    </div>
  `;
};

function getTranslation(key, language) {
  const translations = {
    "mehr anzeigen": {
      en: "Show more",
      fr: "Voir plus",
      de: "Mehr anzeigen",
    },
    "Alle ergebnisse Anzeigen": {
      en: "All results",
      fr: "Tous les résultats",
      de: "Alle Ergebnisse Anzeigen",
    },
  };

  return translations[key]?.[language] || key;
}

function generateProductHTML(products, productsT, collections) {
  const language = document.documentElement.lang || "en"; // Determine the language from the HTML `lang` attribute
  const showMoreText = getTranslation("mehr anzeigen", language);

  // Always try to show up to 2 collections first
  const collectionsToShow =
    collections && collections.length >= 2
      ? collections.slice(0, 2)
      : (collections || []).slice(0, collections ? collections.length : 0);

  // Fill the rest with products, up to a total of 7 items
  const remainingSlots = 7 - collectionsToShow.length;
  const productsToShow = products.slice(0, remainingSlots);

  // Combine collections and products
  const collectionsHTML = collectionsToShow
    .map((collection, index) => {
      return `
        <li id="predictive-search-option-collection-${
          index + 1
        }" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="${
            collection.url
          }" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text js-collection" tabindex="-1">
            <img class="predictive-search__image" src="${
              collection.featured_image.url || ""
            }" alt="${collection.title}" width="50" height="28.125" />
            <div class="predictive-search__item-content">
              <p class="predictive-search__item-heading h5">${
                collection.title
              }</p>
              <div class="predictive-search__item-meta">
                <div class="predictive-search__item-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                    <path d="M16.7071 8.70711C17.0976 8.31658 17.0976 7.68342 16.7071 7.29289L10.3431 0.928932C9.95262 0.538408 9.31946 0.538408 8.92893 0.928932C8.53841 1.31946 8.53841 1.95262 8.92893 2.34315L14.5858 8L8.92893 13.6569C8.53841 14.0474 8.53841 14.6805 8.92893 15.0711C9.31946 15.4616 9.95262 15.4616 10.3431 15.0711L16.7071 8.70711ZM0 9H16V7H0L0 9Z" fill="black"/>
                  </svg>
                </div>
              </div>
            </div>
          </a>
        </li>
      `;
    })
    .join("");

  const productsHTML = productsToShow
    .map((product, index) => {
      const currentPrice = product.price_min ?? "N/A";
      const compareAtPrice = product.compare_at_price_max;
      const discount = compareAtPrice
        ? ((parseFloat(compareAtPrice) - parseFloat(product.price)) /
            parseFloat(compareAtPrice)) *
          100
        : 0;
      const isOnSale = compareAtPrice && discount >= 5;
      const vendorTitle = product.vendor ? `${product.vendor} ` : "";

      return `
        <li id="predictive-search-option-product-${
          collectionsToShow.length + index + 1
        }" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="${
            product.url
          }" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text js-product" tabindex="-1">
            <img class="predictive-search__image" src="${product.image}" alt="${
        product.title
      }" width="50" height="28.125" />
            <div class="predictive-search__item-content">
              <p class="predictive-search__item-heading h5">${vendorTitle}${
        product.title
      }</p>
              <div class="predictive-search__item-meta">
                ${
                  isOnSale
                    ? `<span class="predictive-search__badge">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path fill-rule="evenodd" clip-rule="evenodd" d="M9.11033 1.44034C8.92159 1.61111 8.72327 1.77098 8.51631 1.91915C8.1587 2.15916 7.75669 2.32476 7.33428 2.40876C7.15068 2.44476 6.95867 2.46036 6.57586 2.49036C5.61464 2.56717 5.13343 2.60557 4.73262 2.74717C4.27433 2.90898 3.85807 3.17134 3.51441 3.51501C3.17074 3.85868 2.90838 4.27493 2.74657 4.73322C2.60496 5.13403 2.56656 5.61524 2.48976 6.57647C2.47667 6.83059 2.44943 7.08379 2.40816 7.33489C2.32416 7.7573 2.15855 8.15931 1.91855 8.51692C1.81415 8.67292 1.68934 8.81932 1.43974 9.11093C0.81452 9.84535 0.501312 10.2126 0.317708 10.5966C-0.105903 11.4846 -0.105903 12.5166 0.317708 13.4046C0.501312 13.7886 0.81452 14.1559 1.43974 14.8903C1.68934 15.1819 1.81415 15.3283 1.91855 15.4843C2.15855 15.8419 2.32416 16.2439 2.40816 16.6663C2.44416 16.8499 2.45976 17.0419 2.48976 17.4247C2.56656 18.386 2.60496 18.8672 2.74657 19.268C2.90838 19.7263 3.17074 20.1425 3.51441 20.4862C3.85807 20.8299 4.27433 21.0922 4.73262 21.254C5.13343 21.3956 5.61464 21.434 6.57586 21.5108C6.95867 21.5408 7.15068 21.5564 7.33428 21.5924C7.75669 21.6764 8.1587 21.8432 8.51631 22.0821C8.67232 22.1865 8.81872 22.3113 9.11033 22.5609C9.84475 23.1861 10.212 23.4993 10.596 23.6829C11.484 24.1065 12.516 24.1065 13.404 23.6829C13.788 23.4993 14.1553 23.1861 14.8897 22.5609C15.1813 22.3113 15.3277 22.1865 15.4837 22.0821C15.8413 21.842 16.2433 21.6764 16.6657 21.5924C16.8493 21.5564 17.0413 21.5408 17.4241 21.5108C18.3854 21.434 18.8666 21.3956 19.2674 21.254C19.7257 21.0922 20.1419 20.8299 20.4856 20.4862C20.8293 20.1425 21.0916 19.7263 21.2534 19.268C21.395 18.8672 21.4334 18.386 21.5102 17.4247C21.5402 17.0419 21.5558 16.8499 21.5918 16.6663C21.6758 16.2439 21.8426 15.8419 22.0815 15.4843C22.1859 15.3283 22.3107 15.1819 22.5603 14.8903C23.1855 14.1559 23.4987 13.7886 23.6823 13.4046C24.1059 12.5166 24.1059 11.4846 23.6823 10.5966C23.4987 10.2126 23.1855 9.84535 22.5603 9.11093C22.3895 8.9222 22.2296 8.72387 22.0815 8.51692C21.8417 8.15917 21.6753 7.7574 21.5918 7.33489C21.5506 7.08379 21.5234 6.83059 21.5102 6.57647C21.4334 5.61524 21.395 5.13403 21.2534 4.73322C21.0916 4.27493 20.8293 3.85868 20.4856 3.51501C20.1419 3.17134 19.7257 2.90898 19.2674 2.74717C18.8666 2.60557 18.3854 2.56717 17.4241 2.49036C17.17 2.47727 16.9168 2.45003 16.6657 2.40876C16.2432 2.32535 15.8414 2.15893 15.4837 1.91915C15.277 1.77066 15.0787 1.61081 14.8897 1.44034C14.1553 0.815123 13.788 0.501915 13.404 0.318311C12.9656 0.108763 12.4859 0 12 0C11.5141 0 11.0344 0.108763 10.596 0.318311C10.212 0.501915 9.84475 0.815123 9.11033 1.44034ZM16.5973 7.40449C16.7801 7.58762 16.8827 7.83578 16.8827 8.0945C16.8827 8.35323 16.7801 8.60139 16.5973 8.78452L8.78392 16.5991C8.69489 16.6966 8.58704 16.7749 8.46688 16.8295C8.34672 16.8841 8.21674 16.9138 8.08479 16.9168C7.95284 16.9198 7.82165 16.896 7.69915 16.8469C7.57665 16.7977 7.46538 16.7243 7.37205 16.631C7.27872 16.5376 7.20528 16.4263 7.15615 16.3039C7.10702 16.1814 7.08322 16.0502 7.08619 15.9182C7.08917 15.7863 7.11885 15.6563 7.17346 15.5361C7.22806 15.416 7.30645 15.3081 7.40388 15.2191L15.2161 7.40569C15.3992 7.22293 15.6474 7.12028 15.9061 7.12028C16.1648 7.12028 16.413 7.22293 16.5961 7.40569M16.5565 15.2575C16.5565 15.6028 16.4193 15.934 16.1752 16.1782C15.931 16.4223 15.5998 16.5595 15.2545 16.5595C14.9092 16.5595 14.578 16.4223 14.3338 16.1782C14.0896 15.934 13.9524 15.6028 13.9524 15.2575C13.9524 14.9122 14.0896 14.581 14.3338 14.3368C14.578 14.0926 14.9092 13.9555 15.2545 13.9555C15.5998 13.9555 15.931 14.0926 16.1752 14.3368C16.4193 14.581 16.5565 14.9122 16.5565 15.2575ZM8.74432 10.0494C9.08964 10.0494 9.42082 9.91218 9.66499 9.668C9.90917 9.42382 10.0464 9.09264 10.0464 8.74732C10.0464 8.402 9.90917 8.07082 9.66499 7.82665C9.42082 7.58247 9.08964 7.44529 8.74432 7.44529C8.399 7.44529 8.06782 7.58247 7.82364 7.82665C7.57946 8.07082 7.44229 8.402 7.44229 8.74732C7.44229 9.09264 7.57946 9.42382 7.82364 9.668C8.06782 9.91218 8.399 10.0494 8.74432 10.0494Z" fill="#9B1E20"/>
</svg>
                      </span>`
                    : ""
                }
                <div class="predictive-search__item-prices">
                  ${
                    isOnSale
                      ? `<span class="predictive-search__price predictive-search__price--compare">${compareAtPrice}€</span>`
                      : ""
                  }
                  <span class="predictive-search__price ${
                    isOnSale ? `predictive-search--compare` : ""
                  }">${currentPrice}€</span>
                </div>
                <div class="predictive-search__item-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                    <path d="M16.7071 8.70711C17.0976 8.31658 17.0976 7.68342 16.7071 7.29289L10.3431 0.928932C9.95262 0.538408 9.31946 0.538408 8.92893 0.928932C8.53841 1.31946 8.53841 1.95262 8.92893 2.34315L14.5858 8L8.92893 13.6569C8.53841 14.0474 8.53841 14.6805 8.92893 15.0711C9.31946 15.4616 9.95262 15.4616 10.3431 15.0711L16.7071 8.70711ZM0 9H16V7H0L0 9Z" fill="black"/>
                  </svg>
                </div>
              </div>
            </div>
          </a>
        </li>
      `;
    })
    .join("");

  // Combine collections and products HTML
  const combinedItemsHTML = collectionsHTML + productsHTML;

  return `
    <div class="predictive-search__result-group">
      <div>
        <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">
          Produkte
          <a class="predictive-search__mehr" href="#">${showMoreText}</a>
        </h2>
        <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">
          ${combinedItemsHTML}
        </ul>
      </div>
    </div>
  `;
}

function wrapMarkup(
  keywordsMarkup,
  productsMarkup,
  lastSearchedMarkup,
  bestsellerMarkup,
  queryKey,
  searchForT
) {
  const language = document.documentElement.lang || 'en';

  // Only show the "Tous les résultats" button if there are actual search results
  const hasSearchResults = productsMarkup || queryKey;
  const searchButtonMarkup = hasSearchResults ? `
    <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
      <button
        class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow"
        tabindex="-1"
        role="option"
        aria-selected="false"
      >
        <span data-predictive-search-search-for-text>Alle ergebnisse Anzeigen</span>
      </button>
    </div>
  ` : '';

  return `
    <div id="predictive-search-results" role="listbox">
      <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">
        ${keywordsMarkup}
        ${lastSearchedMarkup}
        ${bestsellerMarkup}
        ${productsMarkup}
      </div>
      ${searchButtonMarkup}
    </div>
  `;
}

document.addEventListener("DOMContentLoaded", () => {
  const predictiveSearchContainer = document.querySelector(
    "[data-predictive-search]"
  );
  const language = document.documentElement.lang || "de";

  if (predictiveSearchContainer) {
    predictiveSearchContainer.addEventListener("click", (event) => {
      const mehrAnzeigenLink = event.target.closest(".predictive-search__mehr");
      if (mehrAnzeigenLink) {
        event.preventDefault();
        const searchInput = document.querySelector(".search__input");
        if (searchInput) {
          const searchQuery = encodeURIComponent(searchInput.value.trim());
          if (searchQuery) {
            const searchUrl = `/search?q=${searchQuery}&options[prefix]=last`;
            window.location.href = searchUrl;
          } else {
            console.warn("No search query found in .search__input element.");
          }
        } else {
          console.warn("No .search__input element found.");
        }
      }
    });
  }
});

document.addEventListener("keydown", (event) => {
  // Only proceed if the event is for alphanumeric keys and not inside an input or textarea
  const isAlphanumeric = /^[a-zA-Z0-9]$/.test(event.key);
  const isModifierKey = event.ctrlKey || event.metaKey || event.altKey;
  const isInInput = ["INPUT", "TEXTAREA", "SELECT"].includes(
    document.activeElement.tagName
  );
  const isContentEditable = document.activeElement.isContentEditable;
  const isInShadowContainer =
    event.target.closest("#opus-shadow-container") !== null;

  if (
    isAlphanumeric &&
    !isModifierKey &&
    !isInInput &&
    !isContentEditable &&
    !isInShadowContainer
  ) {
    // Find the search input
    const searchInput = document.querySelector(".search__input");

    if (searchInput) {
      // Focus the search input
      searchInput.focus();

      // Clear any existing text if needed
      // searchInput.value = "";

      // We don't need to manually add the key as focusing the input will capture the keypress
    }
  }
});

function generateCollectionsHTML(
  collections,
  fallbackImage = "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png?v=1530129081"
) {
  if (!collections.length) return "";

  const items = collections
    .map(
      (collection, index) => `
    <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-collection-${
      index + 1
    }">
      <a href="${
        collection.url
      }" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text js-collection" tabindex="-1">
        <img class="predictive-search__image" src="${
          collection.featured_image?.url || fallbackImage
        }" alt="${collection.title}" width="50" height="28.125" />
        <div class="predictive-search__item-content">
          <p class="predictive-search__item-heading h5">${collection.title}</p>
        </div>
      </a>
    </li>
  `
    )
    .join("");

  return `
    <div class="predictive-search__result-group">
      <div>
        <h2 id="predictive-search-collections" class="predictive-search__heading text-body caption-with-letter-spacing">
          Marken und Kategorien
        </h2>
        <ul id="predictive-search-results-collections-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-collections">
          ${items}
        </ul>
      </div>
    </div>
  `;
}

function generatePagesHTML(pages) {
  if (!pages.length) return "";

  const items = pages
    .map(
      (page, index) => `
    <li class="predictive-search__list-item" role="option" aria-selected="false" id="predictive-search-page-${
      index + 1
    }">
      <a href="${
        page.url
      }" class="predictive-search__item predictive-search__item--link link link--text" tabindex="-1">
        <div class="predictive-search__item-content">
          <p class="predictive-search__item-heading h5">${page.title}</p>
        </div>
      </a>
    </li>
  `
    )
    .join("");

  return `
    <div class="predictive-search__result-group">
      <div>
        <h2 id="predictive-search-pages" class="predictive-search__heading text-body caption-with-letter-spacing">
          Seiten und Blogbeiträge
        </h2>
        <ul id="predictive-search-results-pages-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-pages">
          ${items}
        </ul>
      </div>
    </div>
  `;
}
